import { gambits } from '@/core/gambits';
import type { OpeningHourEntry } from '@/core/helpers/opening-hours.helper';
import { getWeekdayInTimeZone, isProposalOutsideOpeningHours } from '@/core/helpers/opening-hours.helper';
import { useLocationStore } from '@/stores/location.store';
import { ref } from 'vue';

const openingHoursCache = new Map<string, OpeningHourEntry[] | null>();

export async function resolveLocationOpeningHours(
  locationId?: string | null,
  known?: OpeningHourEntry[] | null,
): Promise<OpeningHourEntry[] | null> {
  if (known !== undefined) return known;
  if (!locationId) return null;

  const locationStore = useLocationStore();
  if (locationStore.location?.id === locationId && locationStore.location.openingHours !== undefined) {
    return locationStore.location.openingHours;
  }

  if (openingHoursCache.has(locationId)) {
    return openingHoursCache.get(locationId) ?? null;
  }

  try {
    const result = await gambits.locationService.getLocation(locationId);
    if (!result.data) return null;

    const hours = result.data.openingHours ?? null;
    openingHoursCache.set(locationId, hours);
    return hours;
  } catch {
    return null;
  }
}

export function useClosedSpotNotice() {
  const isOpen = ref(false);
  const hours = ref<OpeningHourEntry[]>([]);
  const highlightDay = ref(0);
  let resolver: ((proceed: boolean) => void) | null = null;

  function settle(proceed: boolean) {
    isOpen.value = false;
    const resolve = resolver;
    resolver = null;
    resolve?.(proceed);
  }

  async function confirmIfOutside(options: {
    locationId?: string | null;
    openingHours?: OpeningHourEntry[] | null;
    start: Date;
    timeZone: string;
  }): Promise<boolean> {
    const openingHours = await resolveLocationOpeningHours(options.locationId, options.openingHours);
    if (!isProposalOutsideOpeningHours({ start: options.start, timeZone: options.timeZone, openingHours })) {
      return true;
    }

    hours.value = openingHours ?? [];
    highlightDay.value = getWeekdayInTimeZone(options.start, options.timeZone);
    isOpen.value = true;

    return new Promise((resolve) => {
      resolver = resolve;
    });
  }

  return {
    isOpen,
    hours,
    highlightDay,
    confirmIfOutside,
    createAnyways: () => settle(true),
    change: () => settle(false),
  };
}
